import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import Button from "../../components/button/Button";
import "./giftcardrates.css";
import GiftCardRateTable from "../../components/giftcardratesTable/GiftCardRateTable";
import NavBar from "../../Nav Bar/NavBar";
import ColoredLogo from "../../assets/ColoredLogo.svg";
import ResultNotFound from "../resultNotFound/ResultNotFound";
import ImageSelect from "../../components/modals/ImageSelect";
import request from "../../request";

const DEFAULT_COUNTRY_ID = "14";
const API_BASE = "https://api2.giftcard8.com";
// const API_BASE = "https://test-giftcard8-api.gcard8.com";
const GiftCardRates = () => {
  const [cardsRate, setCardsRate] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredRates, setFilteredRates] = useState([]);
  const [country, setCountry] = useState({ name: "Nigeria", currency_name: "NGN", currency_symbol: "₦" });
  const [loading, setLoading] = useState(true);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const contentRef = useRef(null);
  const [countryId, setCountryId] = useState(DEFAULT_COUNTRY_ID);
  const [isLocating, setIsLocating] = useState(true);
  const [countryList, setCountryList] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // 1. Fetch country list
  useEffect(() => {
    setLoadingCountries(true);
    request(`${API_BASE}/gc/public/countrylist`, "POST", { app_version: "2.6.3" })
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setCountryList(list);
      })
      .catch((err) => {
        console.error("Country list fetch failed:", err);
      })
      .finally(() => {
        setLoadingCountries(false);
      });
  }, []);

  // 2. Detect location on mount
  useEffect(() => {
    const detectLocation = async () => {
      try {
        const res = await request(
          `${API_BASE}/gc/public/detectCountry`,
          "POST",
          {},
        );

        const detectedData = res?.data;
        if (detectedData) {
          setCountry(detectedData);
          if (detectedData.id != null) {
            setCountryId(String(detectedData.id));
          }
        } else {
          console.warn(
            `detectCountry returned no usable data, using default country_id ${DEFAULT_COUNTRY_ID}.`,
          );
        }
      } catch (err) {
        console.warn(
          `Country detection failed, using default country_id ${DEFAULT_COUNTRY_ID}:`,
          err,
        );
      } finally {
        setIsLocating(false);
      }
    };

    detectLocation();
  }, []);

  // Country selected in the dropdown — affects ONLY the rates (and their currency symbol).
  // The page heading/content always stays tailored to the IP-detected `country`.
  const selectedRateCountry =
    countryList.find((c) => String(c.id) === String(countryId)) || country;
  const rateCurrencySymbol =
    selectedRateCountry?.currency_symbol ||
    selectedRateCountry?.symbol ||
    country?.currency_symbol ||
    "₦";

  // 3. Fetch GiftCard Rates whenever countryId changes
  useEffect(() => {
    if (isLocating) return;

    setLoading(true);

    request(`${API_BASE}/gc/card/list`, "POST", {
      country_id: countryId,
    })
      .then((res) => {
        const data = res?.data || [];
        setCardsRate(data);
        setFilteredRates(data);
      })
      .catch((error) => {
        console.error("API Request Failed:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [countryId, isLocating]);

  // Handle manual country selection from dropdown
  const handleCountryChange = (selectedId) => {
    // Only change the rates country; do not touch the detected `country` used for page content.
    setCountryId(selectedId);
  };

  const countryOptions = countryList.map((c) => ({
    value: String(c.id),
    label: c.name,
    image: c.image || null,
  }));

  // Filter results based on searchQuery
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredRates(cardsRate);
      return;
    }

    const filtered = cardsRate.filter((rate) =>
      rate.category_name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    setFilteredRates(filtered);
  }, [searchQuery, cardsRate]);

  const [currentDateTime, setCurrentDateTime] = useState("");

  useEffect(() => {
    const now = new Date();
    const formattedDateTime = `${now.getFullYear()}-${String(
      now.getMonth() + 1,
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} / (${String(
      now.getHours(),
    ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(
      now.getSeconds(),
    ).padStart(2, "0")})`;
    setCurrentDateTime(formattedDateTime);
  }, []);

  const handleFocus = () => setIsSearchFocused(true);
  const handleBlur = () => setIsSearchFocused(false);
  const handleInput = () => {
    if (contentRef.current) {
      setSearchQuery(contentRef.current.innerText);
    }
  };

  return (
    <>
      <Helmet>
        <title>Best Gift Card Rates | GiftCard8</title>
        <link rel="canonical" href="https://giftcard8.com/best-rates" />
      </Helmet>
      <NavBar linkColor="#000" logo={ColoredLogo} bg="#fff" />

      <div
        className="container py-3 d-flex flex-column justify-content-center align-items-center"
        style={{ marginTop: "100px" }}
      >
        <h1 className="w-100 text-center heading">
          The Best Gift Card Rates In {country.name || "Nigeria"}
        </h1>
        <p className="paragraph ">
          Gift card rates refer to the amount of money you receive when you
          exchange a gift card for cash. In {country.name || "Nigeria"}, these rates are usually
          calculated as the {country.currency_name || "NGN"} value you get for every dollar, pound, or euro
          on a gift card. Because gift cards are resold on international
          markets, their value is influenced by demand, exchange rates, and the
          ease with which the card can be redeemed. This is why two gift cards
          with the same face value can produce different payouts.
          <br />
          <br />
          Gift card rate differ by brand because not all gift cards have the
          same demand. Popular cards such as Amazon, Apple, Steam, Google Play,
          and Razer Gold often attract higher rates due to their wide
          accapetance and fast resale potential. Cards with limited usage, lower
          demand, or stricter redemption rules usually have lower rates.
          Additionally, the country or currency of the gift card is also
          important. For example, US-issued gift cards typically attract better
          rates than cards from less active markets because they are easier to
          redeem and resell internationally.
          <br />
          <br />
          At Giftcard8, rates are determined using real-time market data and
          demand trends. Our system analyzes multiple factors, including card
          brand, card type (physical or e-code), denomination, and current
          market demand, before assigning a rate. This allows us to offer
          competitive and transparent pricing while protecting users from sudden
          losses or unfair deductions. The rate displayed on this page reflects
          the best available value at that moment, helping users make informed
          decisions before trading.
          <br />
          <br />
          Gift card rates in {country.name || "Nigeria"} fluctuate frequently due to several local
          and global factors. Changes in foreign exchange rates, especially the {country.currency_name || "NGN"}'s performance against major currencies, directly affect payouts.
          Seasonal demand, international buying trends, and supply volume also
          influence daily rate movements. During high-demand periods such as
          festive seasons or major sales events, rates may increase, while
          oversupply or low demand can cause temporary drops.
          <br />
          <br />
          This page is designed to help you easily check the best gift card
          rates in {country.name || "Nigeria"} today. By using the rate search tool below, you can
          instantly see the current value of your gift card and trade with
          confidence, knowing the rate is based on real market conditions.
        </p>
        <div className="d-flex justify-content-center align-items-center w-100 w-sm-100 w-md-50">
          <Button
            className="w-25 responsive-button hover-effect contBtn"
            actionType="redirect"
            redirectUrl="https://dashboard.giftcard8.com/account"
          >
            Sell Now
          </Button>
        </div>
      </div>

      <div
        className="container mb-5 search-container d-flex align-items-center SGRSection"
        style={{ width: "85%" }}
      >
        <div className="input-group d-flex align-items-center mt-1">
          <i className="bi bi-search me-2"></i>
          <div
            className="search-div"
            style={{
              width: "96%",
              borderRadius: ".375rem",
              minHeight: "30px",
              outline: "none",
            }}
          >
            <div
              ref={contentRef}
              className="form-control rounded-3 text-left"
              contentEditable="true"
              onFocus={handleFocus}
              onBlur={handleBlur}
              onInput={handleInput}
              suppressContentEditableWarning={true}
              style={{
                border: "none",
                outline: "none",
                flex: 1,
                height: "40px",
                fontSize: "18px",
                backgroundColor: "transparent",
                color: searchQuery.trim() ? "black" : "#6c757d",
              }}
            >
              {!isSearchFocused && !searchQuery && "Search giftcards"}
            </div>
          </div>
        </div>
      </div>

      <div className="container mb-4 SGRSection2" style={{ width: "87%" }}>
        <div className="row align-items-center w-100">
          <div className="col-12 col-md-6">
            <h1 className="rates-title text-center text-md-start">Gift Card Rates</h1>
          </div>
          <div className="col-12 col-md-6 d-flex justify-content-center justify-content-md-end gap-2 align-items-center mt-3 mt-md-0">
            <div className="bg-dark rounded-3 p-2 p-md-3 hover-effect">
              <h6 className="m-0 px-2 fs-6 text-center text-md-start" style={{ color: "#fff" }}>
                Current Rates:{" "}
                <span className="fw-bold">{currentDateTime}</span>
              </h6>
            </div>
          </div>
        </div>

        {/* Select Country dropdown below Gift Card Rates & Current Rates badge */}
        <div className="row mt-3 mt-md-4 w-100">
          <div className="col-12 d-flex justify-content-center justify-content-md-start">
            <div className="country-select-wrapper">
              <label className="country-select-label" htmlFor="country-select">
                Select Country
              </label>
              <ImageSelect
                id="country-select"
                options={countryOptions}
                value={countryId}
                onChange={handleCountryChange}
                placeholder="Select Country"
                disabled={loadingCountries || countryList.length === 0}
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div
            style={{
              display: "flex",
              gap: "20px",
              alignItems: "center",
              padding: "20px",
              backgroundColor: "#f0f0f0",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                width: "100px",
                height: "100px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
              }}
            ></div>
            <div
              style={{
                width: "200px",
                height: "20px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
              }}
            ></div>
            <div
              style={{
                width: "300px",
                height: "15px",
                backgroundColor: "#e0e0e0",
                marginTop: "10px",
                borderRadius: "4px",
              }}
            ></div>
          </div>
        </div>
      ) : filteredRates.length > 0 ? (
        filteredRates.map((rate) => (
          <GiftCardRateTable
            key={rate.category_id}
            logo={rate.category_logo_img}
            cardName={rate.category_name}
            list={rate.list}
            currencySymbol={rateCurrencySymbol}
          />
        ))
      ) : (
        <ResultNotFound />
      )}

      <div className="mb-5"></div>
    </>
  );
};

export default GiftCardRates;
